import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export default function FranchiseBreakPanel({ stationId, date, refreshKey }) {
  const { getModulePermission } = useAuth();
  const canEditAdvertising = getModulePermission('advertising')?.can_edit === true;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [localBreaks, setLocalBreaks] = useState([]);
  const [fillerAssets, setFillerAssets] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState('');
  const [adConflicts, setAdConflicts] = useState([]);

  const load = useCallback(async () => {
    if (!stationId) {
      setData(null);
      return;
    }
    try {
      const [response, assetsResponse, conflictsResponse] = await Promise.all([
        axios.get(
          `${API_URL}/api/syndication/stations/${stationId}/break-status`,
          { params: { date }, withCredentials: true }
        ),
        axios.get(`${API_URL}/api/assets`, {
          params: { station_id: stationId, playout_only: true },
          withCredentials: true,
        }),
        axios.get(`${API_URL}/api/syndication/stations/${stationId}/ad-conflicts`, {
          params: { date },
          withCredentials: true,
        }).catch((requestError) => {
          if ([403, 404].includes(requestError.response?.status)) {
            return { data: { conflicts: [] } };
          }
          throw requestError;
        }),
      ]);
      setData(response.data);
      setLocalBreaks(Array.isArray(response.data.available_local_breaks)
        ? response.data.available_local_breaks
        : []);
      setFillerAssets(Array.isArray(assetsResponse.data) ? assetsResponse.data : []);
      setAdConflicts(Array.isArray(conflictsResponse.data)
        ? conflictsResponse.data
        : (Array.isArray(conflictsResponse.data?.conflicts)
          ? conflictsResponse.data.conflicts
          : []));
      setDrafts(Object.fromEntries((response.data.policies || []).map((policy) => [
        String(policy.source_break_id),
        {
          mode: policy.mode || 'parent',
          local_break_id: policy.local_break_id || '',
          use_local_duration: policy.use_local_duration === true,
          filler_asset_id: policy.filler_asset_id || '',
        },
      ])));
      setError('');
    } catch (requestError) {
      if (![403, 404].includes(requestError.response?.status)) {
        setError(requestError.response?.data?.message || requestError.message);
      }
      setData(null);
      setAdConflicts([]);
    }
  }, [stationId, date, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const updateDraft = (policy, changes) => {
    const key = String(policy.source_break_id);
    setDrafts((current) => ({
      ...current,
      [key]: { ...(current[key] || policy), ...changes },
    }));
  };

  const savePolicy = async (policy) => {
    try {
      setSaving(String(policy.source_break_id));
      const draft = drafts[String(policy.source_break_id)] || policy;
      await axios.put(
        `${API_URL}/api/syndication/links/${data.link.link_id}/break-policies/${policy.source_break_id}`,
        {
          mode: draft.mode,
          local_break_id: draft.local_break_id || null,
          use_local_duration: draft.use_local_duration === true,
          filler_asset_id: draft.filler_asset_id || null,
        },
        { withCredentials: true }
      );
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message);
    } finally {
      setSaving('');
    }
  };

  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  if (!data?.linked) return null;

  return (
    <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid rgba(139,92,246,0.45)', mb: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ color: '#c4b5fd' }}>Franchise local commercial breaks</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Break positions come from parent tenant {data.link.parent_tenant_id}. Local inventory replaces the
          parent only where the policy is Local. Mappings are limited to this shared destination station
          (station {data.link.destination_station_id}); an empty local or regional placeholder safely falls back
          to parent content.
        </Typography>
        {adConflicts.map((conflict) => (
          <Alert severity="warning" key={conflict.id} sx={{ mb: 1 }}>
            Mandatory franchise advertising requires {Number(conflict.required_duration || 0)}s in
            {' '}{conflict.log_date} hour {String(conflict.log_hour ?? 0).padStart(2, '0')}, but the mapped
            local break provides {Number(conflict.available_duration || 0)}s. Playout expands the effective
            break so the parent spot is not dropped.
          </Alert>
        ))}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Parent break</TableCell>
                <TableCell>Local slot</TableCell>
                <TableCell>Fill status for {date}</TableCell>
                <TableCell>Playout policy</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.policies || []).map((policy) => {
                const filled = Number(policy.filled_spots || 0);
                const regionalSpots = Number(policy.regional_spots || 0);
                const filledRegions = Number(policy.filled_regions || 0);
                const draft = drafts[String(policy.source_break_id)] || policy;
                const eligibleLocalBreaks = localBreaks.filter((item) =>
                  !item.syndication_source_break_id
                    || String(item.syndication_source_break_id) === String(policy.source_break_id));
                const selectedLocalBreak = eligibleLocalBreaks.find((item) =>
                  String(item.id) === String(draft.local_break_id));
                return (
                  <TableRow key={policy.source_break_id}>
                    <TableCell>
                      {String(policy.hour ?? 0).padStart(2, '0')}:{String(policy.minute ?? 0).padStart(2, '0')}
                      {' · '}{policy.break_name || `Source #${policy.source_break_id}`}
                      {policy.parent_break_number ? ` · #${policy.parent_break_number}` : ''}
                    </TableCell>
                    <TableCell>
                      {policy.local_break_name || (policy.local_break_id
                        ? `Break ${policy.local_break_id}`
                        : 'Not mapped')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={(filled + regionalSpots) > 0 || (policy.mode === 'filler' && policy.filler_asset_id)
                          ? 'success'
                          : 'warning'}
                        label={policy.mode === 'filler' && policy.filler_asset_id
                          ? 'Configured local filler'
                          : filled > 0
                          ? `${filled} station spot${filled === 1 ? '' : 's'}`
                          : regionalSpots > 0
                            ? `${regionalSpots} spot${regionalSpots === 1 ? '' : 's'} across ${filledRegions} region${filledRegions === 1 ? '' : 's'}`
                            : 'Empty · parent fallback'}
                      />
                    </TableCell>
                    <TableCell>
                      {canEditAdvertising ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 220 }}>
                          <FormControl size="small">
                            <InputLabel>Policy</InputLabel>
                            <Select
                              value={draft.mode}
                              label="Policy"
                              onChange={(event) => updateDraft(policy, { mode: event.target.value })}
                            >
                              <MenuItem value="parent">Parent commercials</MenuItem>
                              <MenuItem value="local">Local opt-out</MenuItem>
                              <MenuItem value="filler">Local filler</MenuItem>
                            </Select>
                          </FormControl>
                          {draft.mode === 'local' && (
                            <>
                              <FormControl size="small">
                                <InputLabel>Local break</InputLabel>
                                <Select
                                  value={draft.local_break_id || ''}
                                  label="Local break"
                                  onChange={(event) => updateDraft(policy, { local_break_id: event.target.value })}
                                >
                                  {eligibleLocalBreaks.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                      {String(item.hour ?? 0).padStart(2, '0')}:
                                      {String(item.minute ?? 0).padStart(2, '0')} · {item.name}
                                      {item.break_id ? ` · #${item.break_id}` : ''}
                                      {item.syndication_source_break_id ? ' (parent placeholder)' : ''}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControlLabel
                                control={(
                                  <Switch
                                    size="small"
                                    checked={draft.use_local_duration === true}
                                    onChange={(event) => updateDraft(policy, {
                                      use_local_duration: event.target.checked,
                                    })}
                                  />
                                )}
                                label={selectedLocalBreak?.max_duration
                                  ? `Use local maximum (${selectedLocalBreak.max_duration}s)`
                                  : 'Use local break maximum'}
                              />
                              {draft.use_local_duration === true && (
                                <Alert severity="warning" sx={{ py: 0 }}>
                                  A different length shifts following parent items until the next timed resync point.
                                </Alert>
                              )}
                            </>
                          )}
                          {draft.mode === 'filler' && (
                            <FormControl size="small">
                              <InputLabel>Filler asset</InputLabel>
                              <Select
                                value={draft.filler_asset_id || ''}
                                label="Filler asset"
                                onChange={(event) => updateDraft(policy, { filler_asset_id: event.target.value })}
                              >
                                {fillerAssets.map((asset) => (
                                  <MenuItem key={asset.id} value={asset.id}>
                                    {asset.artist ? `${asset.artist} - ` : ''}{asset.title}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={saving === String(policy.source_break_id)}
                            onClick={() => savePolicy(policy)}
                          >
                            Save policy
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ textTransform: 'capitalize' }}>{policy.mode}</Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}
